/*

For cohorts: instance.data.anchorDate.at --> are the on the same day


5-7 --> the number of days that are shown in the UI is limited

~120 days long


Dec 14
Dec 15
Dec 16
Dec 17
Dec 18

Dec 15
Dec 16
Dec 17
Dec 18
Dec 19

Dec 16
Dec 17
Dec 18
Dec 19
Dec 20

*/

import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationStatus,
  IExperience,
  IExperienceTemplate,
  MessageStatus,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { groupBy, keyBy, orderBy } from 'lodash';
import {
  getFullMessageStatusIncludingMostRecentConcreteMessageStatus,
  getMostRecentSuccessfulConcreteMessageOrAnyOther
} from '../../../../../services/experiences/message';
import {
  getAutomationTemplateById,
  getMessageTemplateById,
  getTaskTemplateById
} from '../../../../../services/experiences/template';
import { getFixedStartOrInfinity } from '../../../../../services/experiences/timing';
import { toMoment } from '../../../../../services/time';
import { CalV2ItemAutomation, CalV2ItemMessage, CalV2ItemTask } from './types';

// export const EMPTY_TASK_STATS = (): TaskStats => ({
//   type: 'automations',
//   itemTplId: item.itemTplId,
//   counts: {
//     success: 0,
//     error: 0,
//     ongoing: 0,
//     upcoming: 0
//   },
//   instanceIds: [],
//   startTime: msg?.sendAt,
//   actorKey: msg?.actor
// });

export const CAL_V2_AGGREGATION_DATE_FORMAT = 'YYYY-MM-DD';

export const aggregateForAllDates = (
  ds: Doc<IExperience>[],
  tz: string,
  tpl: Doc<IExperienceTemplate>
) => {
  // iterate over all experiences and see if any item is on such a date

  // First step:
  // - find all the items that happen on a date we are interested in
  // Second step:
  // - group them by cohorts
  // Third step:
  // - sort them according to the requirements (Cohort, time of day, role, timeline item type)
  // - and count the status numbers

  const result: {
    [date: string]: (
      | {
          instanceId: string; // because we want to know the cohort
          type: 'message';
          status: MessageStatus;
          itemTplId: string;
        }
      | {
          instanceId: string; // because we want to know the cohort
          type: 'task';
          status: TaskStatus;
          itemTplId: string;
          // Display-only overdue: the BE only promotes a task to LATE on its
          // dedicated sweep, so an overdue NOT_STARTED/RUNNING task would never
          // otherwise render as an error. Computed from timing.end vs now.
          isOverdue: boolean;
        }
      | {
          instanceId: string; // because we want to know the cohort
          type: 'automation';
          status: AutomationStatus;
          itemTplId: string;
        }
    )[];
  } = {};

  const nowMs = Date.now();

  ds.forEach((d) => {
    d.data.tasks.forEach((task) => {
      if (task.status === TaskStatus.SKIPPED) {
        return;
      }
      const date = toMoment(task.timing.start)
        .tz(tz)
        .format(CAL_V2_AGGREGATION_DATE_FORMAT);
      // then we are interested in this item
      const container = (result[date] = result[date] || []);
      container.push({
        instanceId: d.id,
        type: 'task',
        itemTplId: task.templateId,
        status: task.status,
        // An active (NOT_STARTED/RUNNING) task whose deadline has passed is
        // overdue even though the BE hasn't swept it to LATE yet. LATE is already
        // an error via the switch below; PAST_DEADLINE (optional-only) and the
        // terminal COMPLETE/SKIPPED are intentionally not flagged.
        isOverdue:
          !!task.timing.end &&
          task.timing.end.toMillis() < nowMs &&
          (task.status === TaskStatus.NOT_STARTED ||
            task.status === TaskStatus.RUNNING)
      });
    });

    Object.entries(d.data.messagesStatuses).forEach(([msgId, msgStatus]) => {
      if (msgStatus.status === MessageStatus.SKIPPED) {
        return;
      }
      // Group SENT messages under their ACTUAL send date (the concrete record's
      // `at`), not the scheduled "should-have-sent" time; fall back to the
      // scheduled `timing.at` for not-yet-sent messages. Mirrors the field +
      // fallback in ParticipantDialogComponents/MessageSendDate.tsx.
      const concrete = getMostRecentSuccessfulConcreteMessageOrAnyOther(
        msgId,
        d.data.messages,
        d.data.messagesStatuses
      );
      const actualSendAt = concrete && 'at' in concrete ? concrete.at : null;
      const date = toMoment(actualSendAt ?? msgStatus.timing.at)
        .tz(tz)
        .format(CAL_V2_AGGREGATION_DATE_FORMAT);
      // then we are interested in this item
      const container = (result[date] = result[date] || []);
      container.push({
        instanceId: d.id,
        type: 'message',
        itemTplId: msgId,
        status: msgStatus.status
      });
    });

    Object.entries(d.data.automations).forEach(([automationId, automation]) => {
      if (automation.status === AutomationStatus.SKIPPED) {
        return;
      }
      const date = toMoment(automation.timing.at)
        .tz(tz)
        .format(CAL_V2_AGGREGATION_DATE_FORMAT);
      // then we are interested in this item
      const container = (result[date] = result[date] || []);
      container.push({
        instanceId: d.id,
        type: 'automation',
        itemTplId: automationId,
        status: automation.status
      });
    });
  });

  const instancesById = keyBy(ds, (d) => d.id);
  const getCohortDate = (instanceId: string) => {
    const instance = instancesById[instanceId];
    // this could be cached so that we don't need to recompute all the time
    return toMoment(instance.data.anchorDate.at)
      .tz(tz)
      .format(CAL_V2_AGGREGATION_DATE_FORMAT);
  };

  // Second step:
  // - group them by cohorts
  // Third step:
  // - and count the status numbers
  // Forth step:
  // - sort them according to the requirements (Cohort, time of day, role, timeline item type)
  const unsorted = Object.entries(result).map(([date, resultForDate]) => {
    if (!resultForDate) {
      // return something empty
      return {
        date,
        groups: []
      };
    }
    const groupedByCohortDate = groupBy(resultForDate, (r) => {
      const { instanceId } = r;
      const cohortDate = getCohortDate(instanceId);
      return cohortDate;
    });

    return {
      date,
      groups: Object.entries(groupedByCohortDate).map(([cohortDate, items]) => {
        const itemsById: {
          [itemTplId: string]: (
            | ((CalV2ItemTask | CalV2ItemMessage) & { actorKey: string })
            | CalV2ItemAutomation
          ) & { startTime: number };
        } = {};
        items.forEach((item) => {
          if (item.type === 'message') {
            const msg = getMessageTemplateById(tpl, item.itemTplId);
            const instance = instancesById[item.instanceId];
            const container = (itemsById[item.itemTplId] = itemsById[
              item.itemTplId
            ] || {
              type: 'message',
              itemTplId: item.itemTplId,
              counts: {
                success: 0,
                error: 0,
                ongoing: 0,
                upcoming: 0
              },
              instanceIds: [],
              startTime: msg && getFixedStartOrInfinity(msg.timing),
              actorKey: msg?.actor
            });
            const fullStatus = getFullMessageStatusIncludingMostRecentConcreteMessageStatus(
              instance,
              item.itemTplId
            );
            switch (fullStatus) {
              case 'OK':
                container.counts.success++;
                container.instanceIds.push(item.instanceId);
                return;
              case 'BOUNCED':
              case 'ERROR':
                container.counts.error++;
                container.instanceIds.push(item.instanceId);
                return;
              case 'UPCOMING':
                container.counts.upcoming++;
                container.instanceIds.push(item.instanceId);
                return;
              default:
                return;
            }
          }
          if (item.type === 'task') {
            const task = getTaskTemplateById(tpl, item.itemTplId);

            const container = (itemsById[item.itemTplId] = itemsById[
              item.itemTplId
            ] || {
              type: 'task',
              itemTplId: item.itemTplId,
              counts: {
                success: 0,
                error: 0,
                ongoing: 0,
                upcoming: 0
              },
              instanceIds: [],
              startTime: task && getFixedStartOrInfinity(task.timing),
              actorKey: task?.actor
            });

            // An overdue active task counts as an error even before the BE sweep
            // flips it to LATE (see isOverdue above).
            if (item.isOverdue) {
              container.counts.error++;
              container.instanceIds.push(item.instanceId);
              return;
            }

            switch (item.status) {
              case TaskStatus.COMPLETE:
                container.counts.success++;
                container.instanceIds.push(item.instanceId);
                return;
              case TaskStatus.RUNNING:
                container.counts.ongoing++;
                container.instanceIds.push(item.instanceId);
                return;
              case TaskStatus.NOT_STARTED:
                container.counts.upcoming++;
                container.instanceIds.push(item.instanceId);
                return;
              case TaskStatus.LATE:
                container.counts.error++;
                container.instanceIds.push(item.instanceId);
                return;
              default:
                return;
            }
          }
          if (item.type === 'automation') {
            const automation = getAutomationTemplateById(tpl, item.itemTplId);
            const container = (itemsById[item.itemTplId] = itemsById[
              item.itemTplId
            ] || {
              type: 'automation',
              itemTplId: item.itemTplId,
              counts: {
                success: 0,
                error: 0,
                ongoing: 0,
                upcoming: 0
              },
              instanceIds: [],
              startTime:
                automation && getFixedStartOrInfinity(automation.timing),
              status: ''
            });

            switch (item.status) {
              case AutomationStatus.DONE:
                container.counts.success++;
                container.instanceIds.push(item.instanceId);
                return;
              case AutomationStatus.UPCOMING:
                container.counts.upcoming++;
                container.instanceIds.push(item.instanceId);
                return;
              case AutomationStatus.ERROR:
                container.counts.error++;
                container.instanceIds.push(item.instanceId);
                return;
              default:
                return;
            }
          }
        });

        return {
          cohortDate,
          items: Object.values(itemsById)
        };
      })
    };
  });

  unsorted.forEach((x) => {
    x.groups = orderBy(x.groups, (g) => g.cohortDate, 'desc');
    x.groups.forEach((g) => {
      g.items = orderBy(Object.values(g.items), [
        (x) => x.startTime,
        (x) =>
          x.type === 'automation'
            ? 'zzzzzzzzzzzzzzzzz'
            : x.actorKey === PARTICIPANT_ACTOR_KEY
            ? '0'
            : x.actorKey === 'manager' || x.actorKey === 'mentor'
            ? '1'
            : x.actorKey,
        (x) => (x.type === 'message' ? '0' : x.type === 'task' ? '1' : x.type)
      ]);
    });
  });

  return orderBy(unsorted, (x) => x.date, 'asc');
};
